(function () {
  var CONFIG_ELEMENT_ID = 'et-sentry-config';
  var THEME_ASSET_PATH_PATTERN = /\/cdn\/shop\/t\/\d+\/assets\//i;
  var NON_THEME_SAME_ORIGIN_PATH_PATTERNS = [
    /\/cdn\/shopifycloud\//i,
    /\/web-pixels@/i,
    /\/cdn\/wpm\//i,
    /\/extensions\//i
  ];
  // Tokens that uniquely identify Shopify's platform-injected analytics
  // bootstrap (Web Pixels Manager / Trekkie). Shopify renders this code as an
  // inline <script> in the page document via content_for_header, so its stack
  // frames share the page URL as their filename and would otherwise be
  // misclassified as theme-owned inline scripts. None of these tokens appear in
  // first-party theme source, so a frame referencing them is never ours.
  var SHOPIFY_INJECTED_SOURCE_TOKENS = [
    'webPixelsManager',
    '__TREKKIE_SHIM_QUEUE',
    'web-pixels-manager'
  ];
  // Signature of a frame whose "source" is an HTML document rather than
  // JavaScript. In-app browsers (e.g. Instagram) inject or rewrite a <script>
  // whose src resolves to the page URL itself, so the engine fetches the page's
  // own markup and parses it as JS, throwing `SyntaxError: Unexpected token
  // '<'`. The failing frame then carries the same-origin page URL as its
  // filename -- indistinguishable from a real inline theme script by path alone
  // -- but its source line is the document's opening markup, which no
  // first-party theme JavaScript ever is. (SHOPIFY-37V)
  var HTML_DOCUMENT_SOURCE_PATTERN = /^\s*(?:<!doctype\s+html|<html[\s>]|<head[\s>])/i;
  // High-precision automated-traffic signals. Headless engines and scrapers
  // hammer the storefront, choke on an inline <script> (parse-time
  // SyntaxError), and -- because the session is flagged crashed before
  // beforeSend can run and there is no beforeSendSession hook -- drag the
  // crash-free SESSION rate down without representing any real shopper. We bias
  // toward false negatives (miss a bot) over false positives (blind a real
  // user), so every token here is one a genuine shopper effectively never trips.
  var BOT_UA_PATTERN =
    /crawl|spider|slurp|headless|phantomjs|prerender|lighthouse|pagespeed|gtmetrix|bingpreview|puppeteer|playwright|selenium/i;
  // localStorage key for the persistent anonymous shopper id used as the Sentry
  // user when no customer is logged in.
  var ANON_ID_STORAGE_KEY = 'et_sentry_anonymous_id';
  var state = {
    config: null,
    initialized: false
  };

  function createNoopObservability() {
    return {
      captureException: function () { return null; },
      captureMessage: function () { return null; },
      startSpan: function (_context, callback) {
        if (typeof callback === 'function') return callback(null);
        return null;
      },
      startSpanManual: function (_context, callback) {
        if (typeof callback === 'function') return callback(null);
        return null;
      }
    };
  }

  function freezeValue(value) {
    if (typeof Object.freeze === 'function') return Object.freeze(value);
    return value;
  }

  function ensureObservability() {
    if (!window.etObservability || typeof window.etObservability !== 'object') {
      window.etObservability = freezeValue(createNoopObservability());
    }
  }

  function readConfigElement() {
    var configElement = document.getElementById(CONFIG_ELEMENT_ID);
    if (!configElement) return {};

    try {
      return JSON.parse(configElement.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function buildConfig() {
    var baseConfig = readConfigElement();
    var overrideConfig = window.etSentryConfig;

    if (!overrideConfig || typeof overrideConfig !== 'object') {
      overrideConfig = {};
    }

    state.config = Object.assign({}, baseConfig, overrideConfig);
    window.etSentryConfig = state.config;
    return state.config;
  }

  function parseSampleRate(rawValue, fallback) {
    var parsedValue = Number(rawValue);

    if (!Number.isFinite(parsedValue)) parsedValue = fallback;
    if (parsedValue < 0) return 0;
    if (parsedValue > 1) return 1;
    return parsedValue;
  }

  function parseBoolean(rawValue, fallback) {
    if (typeof rawValue === 'boolean') return rawValue;
    if (typeof rawValue === 'number') return rawValue !== 0;
    if (typeof rawValue === 'string') {
      var normalized = rawValue.trim().toLowerCase();

      if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
      if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
    }

    return fallback;
  }

  function isLikelyBot() {
    var nav = window.navigator || {};

    // navigator.webdriver is set true by Selenium/Playwright/Puppeteer and other
    // WebDriver-controlled browsers; genuine shoppers leave it false/undefined.
    if (nav.webdriver === true) return true;

    var ua = typeof nav.userAgent === 'string' ? nav.userAgent : '';
    return BOT_UA_PATTERN.test(ua);
  }

  // Safari before 14.1 (iOS before 14.5) cannot parse class fields, so global.js,
  // cart.js, cart-drawer.js and other theme assets fail to load and every page
  // throws ReferenceErrors (SHOPIFY-386, SHOPIFY-W5). The theme does not support
  // those browsers, so treat them like bots. Sentry's legacy-browser inbound
  // filter can't drop them: its Safari rule only matches the desktop "Safari"
  // family, never iOS "Mobile Safari". On iOS every browser is WebKit, so the OS
  // version is the engine version.
  function isUnsupportedBrowser() {
    var nav = window.navigator || {};
    var ua = typeof nav.userAgent === 'string' ? nav.userAgent : '';
    var minimum = [14, 5];
    var match = /OS (\d+)(?:_(\d+))?[_\d]* like Mac OS X/.exec(ua);

    if (!match) {
      minimum = [14, 1];
      match = /Macintosh.*Version\/(\d+)(?:\.(\d+))?.* Safari\//.exec(ua);
    }
    if (!match) return false;

    var major = Number(match[1]);
    return major < minimum[0] || (major === minimum[0] && Number(match[2] || 0) < minimum[1]);
  }

  function generateAnonymousId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (error) {
      // crypto unavailable -- fall through to the timestamp-based id.
    }

    // Two random segments so the suffix is never empty even if one
    // Math.random() degenerates; the timestamp prefix keeps ids distinct
    // across milliseconds.
    var rand = (
      Math.random().toString(36).slice(2) +
      Math.random().toString(36).slice(2)
    ).slice(0, 12);
    return 'aid-' + Date.now().toString(36) + '-' + rand;
  }

  function getAnonymousId() {
    var storage;
    var existing;

    try {
      storage = window.localStorage;
      existing = storage.getItem(ANON_ID_STORAGE_KEY);
      if (existing) return existing;

      var generated = generateAnonymousId();
      storage.setItem(ANON_ID_STORAGE_KEY, generated);
      return generated;
    } catch (error) {
      // Storage blocked (private mode / pending cookie consent): fall back to a
      // per-pageload id so the event is still attributed to *a* user. Without a
      // user, Sentry reports 0 users-impacted on everything and real,
      // shopper-facing errors cannot be told apart from background noise.
      return generateAnonymousId();
    }
  }

  function buildSentryUser(cfg) {
    var customerId = cfg && cfg.customerId;

    // Logged-in shopper: a stable, non-PII id (the numeric Shopify customer id,
    // never an email). customer.id renders as JSON null when logged out, so a
    // simple null/empty check matches the only two values Liquid can emit.
    // Anonymous shopper: a persistent per-browser id.
    if (customerId != null && customerId !== '') {
      return { id: 'customer-' + customerId };
    }

    return { id: getAnonymousId() };
  }

  function buildIntegrations(replayEnabled, replayMaskAllText, replayBlockAllMedia) {
    var integrations = [];

    if (typeof window.Sentry.browserTracingIntegration === 'function') {
      integrations.push(window.Sentry.browserTracingIntegration());
    }

    if (replayEnabled && typeof window.Sentry.replayIntegration === 'function') {
      integrations.push(
        window.Sentry.replayIntegration({
          maskAllText: replayMaskAllText,
          blockAllMedia: replayBlockAllMedia
        })
      );
    }

    return integrations;
  }

  function getFrameSource(frame) {
    if (!frame || typeof frame !== 'object') return '';
    if (typeof frame.absPath === 'string' && frame.absPath.length > 0) return frame.absPath;
    if (typeof frame.filename === 'string' && frame.filename.length > 0) return frame.filename;
    return '';
  }

  function toAbsoluteFrameSource(source) {
    if (typeof source !== 'string' || source.length === 0) return '';
    if (/^https?:\/\//i.test(source)) return source;
    if (source.indexOf('//') === 0) return window.location.protocol + source;
    if (source.charAt(0) === '/') return window.location.origin + source;
    return '';
  }

  function parseFrameSource(source) {
    var absoluteSource = toAbsoluteFrameSource(source);

    if (!absoluteSource) return null;

    try {
      var parsed = new URL(absoluteSource);

      return {
        host: (parsed.hostname || '').toLowerCase(),
        path: parsed.pathname || ''
      };
    } catch (error) {
      return null;
    }
  }

  function isThemeAssetPath(path) {
    return typeof path === 'string' && THEME_ASSET_PATH_PATTERN.test(path);
  }

  function isAllowedInlinePath(path) {
    var i;

    if (typeof path !== 'string' || path.length === 0) return false;
    if (isThemeAssetPath(path)) return true;

    for (i = 0; i < NON_THEME_SAME_ORIGIN_PATH_PATTERNS.length; i++) {
      if (NON_THEME_SAME_ORIGIN_PATH_PATTERNS[i].test(path)) return false;
    }

    if (path.indexOf('/cdn/') === 0) return false;
    return true;
  }

  function getFrameSourceContext(frame) {
    var parts = [];

    if (!frame || typeof frame !== 'object') return '';
    if (typeof frame.context_line === 'string') parts.push(frame.context_line);
    if (Array.isArray(frame.pre_context)) parts = parts.concat(frame.pre_context);
    if (Array.isArray(frame.post_context)) parts = parts.concat(frame.post_context);

    return parts.join('\n');
  }

  function isShopifyInjectedFrame(frame) {
    // The ContextLines integration runs before beforeSend and populates source
    // context for same-origin inline scripts, letting us tell Shopify's injected
    // analytics bootstrap apart from genuine inline theme scripts (which share
    // the same page URL filename).
    var context = getFrameSourceContext(frame);
    var i;

    if (!context) return false;

    for (i = 0; i < SHOPIFY_INJECTED_SOURCE_TOKENS.length; i++) {
      if (context.indexOf(SHOPIFY_INJECTED_SOURCE_TOKENS[i]) !== -1) return true;
    }

    return false;
  }

  function isHtmlDocumentFrame(frame) {
    // The ContextLines integration populates the failing frame's source line
    // before beforeSend runs. When a <script> src resolved to an HTML document,
    // that line is the document's opening markup (e.g. "<!doctype html>"), so
    // the frame is third-party noise rather than theme-owned code.
    if (!frame || typeof frame !== 'object') return false;
    if (typeof frame.context_line !== 'string') return false;
    return HTML_DOCUMENT_SOURCE_PATTERN.test(frame.context_line);
  }

  function eventHasThemeOwnedFrame(event) {
    var exceptionValues = event && event.exception && event.exception.values;
    var currentHost = (window.location.hostname || '').toLowerCase();
    var i;
    var j;

    if (!Array.isArray(exceptionValues) || exceptionValues.length === 0) return false;

    for (i = 0; i < exceptionValues.length; i++) {
      var exceptionValue = exceptionValues[i] || {};
      var stacktrace = exceptionValue.stacktrace || {};
      var frames = stacktrace.frames;

      if (!Array.isArray(frames) || frames.length === 0) continue;

      for (j = 0; j < frames.length; j++) {
        var frameSource = getFrameSource(frames[j]);
        var parsedSource;

        if (!frameSource) continue;

        parsedSource = parseFrameSource(frameSource);
        if (!parsedSource) continue;

        if (isThemeAssetPath(parsedSource.path)) return true;
        if (parsedSource.host === currentHost && isAllowedInlinePath(parsedSource.path)) {
          // Same-origin inline scripts share the page URL as their filename, so
          // Shopify's platform-injected analytics bootstrap and in-app-browser
          // injected scripts that parse the page's own HTML would otherwise be
          // treated as theme-owned. Exclude them so their errors are dropped.
          if (isShopifyInjectedFrame(frames[j])) continue;
          if (isHtmlDocumentFrame(frames[j])) continue;
          return true;
        }
      }
    }

    return false;
  }

  // Some libraries reject with plain objects instead of real Error instances.
  // The Recharge storefront SDK, for example, throws values shaped like
  // { message, name: 'RechargeRequestError', status: 401 }. Sentry only builds
  // a typed, well-grouped issue from an actual Error -- a non-Error collapses
  // into the opaque "Object captured as exception with keys: ..." issue with no
  // usable stack and a generic title (SHOPIFY-11S). Coerce non-Errors into a
  // real Error that keeps the original name/message and carries the remaining
  // fields, so Sentry groups by the true error type (e.g. RechargeRequestError).
  //
  // `fallbackMessage` is the reporting surface's own label (e.g.
  // "[update-meals] overview-hydration-failed"). It titles the event only when
  // the thrown value carries no message of its own, so callers can hand over the
  // raw value instead of pre-replacing it and destroying the cause
  // (SHOPIFY-3HD).
  function fallbackLabel(fallbackMessage) {
    return typeof fallbackMessage === 'string' && fallbackMessage
      ? fallbackMessage
      : 'Non-Error exception captured';
  }

  function normalizeException(value, fallbackMessage) {
    if (value instanceof Error) return value;

    var fallback = fallbackLabel(fallbackMessage);

    // Primitives (thrown strings, numbers, null) have no name and no stack.
    // Keep the caller label as the title so grouping stays stable per surface,
    // and append the raw value so it stays readable on the event.
    if (!value || typeof value !== 'object') {
      var raw = value === null || value === undefined ? '' : shortValue(value);
      return new Error(raw ? fallback + ': ' + raw : fallback);
    }

    var message =
      typeof value.message === 'string' && value.message ? value.message : fallback;
    var normalized = new Error(message);

    if (typeof value.name === 'string' && value.name) {
      normalized.name = value.name;
    }

    // The remaining own fields (status, code, ...) are NOT copied onto the
    // Error: Sentry serializes only name/message/stack from an exception, so
    // they would never reach the event. describeNonErrorCause() ships them as
    // extras instead.
    return normalized;
  }

  // Keeps a single truncation limit for every raw value we put on an event.
  function shortValue(value) {
    return String(value).slice(0, 200);
  }

  // Path segments that carry shopper data rather than route structure: all
  // digits, long and opaque, or containing anything outside the unreserved URL
  // set (which includes every percent-escape, since `%` is itself outside it).
  var OPAQUE_SEGMENT_PATTERN = /^\d+$|^[A-Za-z0-9._~-]{24,}$|[^A-Za-z0-9._~-]/;

  /**
   * Reduces a URL to a groupable, PII-free route template.
   *
   * The storefront puts zip codes straight into the path
   * (`/api/search/availability/90001`) and emails into query strings, so a raw
   * URL on an event ships a quasi-identifier. Query VALUES are dropped and keys
   * kept: which parameters a request carried is diagnostic, what the shopper
   * typed into them is not ours to store.
   *
   * Lives here rather than in a consumer because Sentry attaches URLs to events
   * through several channels this file owns -- fetch/xhr breadcrumbs, the
   * `request.url` context -- and every one of them needs the same treatment.
   */
  function templateUrl(rawUrl) {
    var absolute;
    var segments;
    var keys = [];
    var i;

    if (typeof rawUrl !== 'string' || !rawUrl) return '';

    try {
      absolute = new URL(rawUrl, window.location && window.location.origin);
    } catch (error) {
      // Not a parseable URL. Never fall back to the raw value -- that is the
      // leak this function exists to close.
      return '';
    }

    segments = (absolute.pathname || '').split('/');
    for (i = 0; i < segments.length; i++) {
      if (segments[i] && OPAQUE_SEGMENT_PATTERN.test(segments[i])) segments[i] = ':id';
    }

    try {
      absolute.searchParams.forEach(function (_value, key) {
        if (keys.indexOf(key) === -1) keys.push(key);
      });
    } catch (error) {
      keys = [];
    }

    return shortValue(
      absolute.origin +
        segments.join('/') +
        (keys.length ? '?' + keys.sort().join('&') : '')
    );
  }

  // Sentry's default breadcrumbs integration records every fetch/xhr URL, and
  // httpContextIntegration records the page URL. Both run regardless of what any
  // consumer asset does, so scrubbing at the report site alone leaves the raw
  // zip on the very same payload -- and on unrelated later events, since
  // breadcrumbs persist on the scope. This is the only place that closes it for
  // all of them at once.
  function scrubBreadcrumb(breadcrumb) {
    if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;

    var data = breadcrumb.data;
    if (data && typeof data === 'object' && typeof data.url === 'string' && data.url) {
      data.url = templateUrl(data.url);
    }

    // Console breadcrumbs carry whatever the theme logged, and the instrumented
    // failure paths log the same URLs they report. Dropping the arg array keeps
    // the message (which says what broke) without the interpolated values.
    if (breadcrumb.category === 'console' && breadcrumb.data) {
      delete breadcrumb.data.arguments;
    }

    return breadcrumb;
  }

  function scrubEventUrls(event) {
    if (event && event.request && typeof event.request.url === 'string') {
      event.request.url = templateUrl(event.request.url);
    }
    return event;
  }

  // Sentry's `extraErrorDataIntegration` would do this, but it is not a
  // synchronous export of the CDN bundle the loader pulls
  // (bundle.tracing.replay.logs.metrics) -- only `lazyLoadIntegration` can
  // reach it, which costs a second remote asset and leaves errors captured
  // before it lands unenriched. So describe what actually threw ourselves, and
  // ship it as extras, so a swallowed non-Error stays diagnosable (SHOPIFY-3HD).
  function describeNonErrorCause(value) {
    if (value instanceof Error) return null;

    var descriptor = { causeType: value === null ? 'null' : typeof value };

    if (value && typeof value === 'object') {
      if (typeof value.name === 'string' && value.name) descriptor.causeName = value.name;
      if (typeof value.message === 'string' && value.message) {
        descriptor.causeMessage = value.message;
      }
      var status = value.status;
      if (status === undefined) status = value.statusCode;
      if (status === undefined) status = value.code;
      if (status !== undefined && status !== null) descriptor.causeStatus = String(status);
      // Capped: a rejection carrying a parsed response body can be very wide,
      // and Sentry truncates the extra anyway.
      descriptor.causeKeys = shortValue(Object.keys(value).slice(0, 20).join(','));
    } else if (value !== null && value !== undefined) {
      descriptor.causeValue = shortValue(value);
    }

    return descriptor;
  }

  function createObservability() {
    return freezeValue({
      /**
       * Captures an exception with optional scope context.
       *
       * @param {*} error Thrown value. Pass it RAW -- non-Errors are typed by
       *   normalizeException() and described by describeNonErrorCause(); wrapping
       *   it yourself destroys the cause (SHOPIFY-3HD).
       * @param {Object} [context]
       * @param {Object} [context.tags] Sentry scope tags.
       * @param {Object} [context.extra] Sentry scope extras.
       * @param {string} [context.level] Sentry severity level.
       * @param {string[]} [context.fingerprint] Sentry grouping key. Pass a
       *   non-empty array of non-empty strings to override the default
       *   stack-trace grouping when one report site emits events that must not
       *   share an issue (SHOPIFY-3H9). Anything else is ignored.
       * @param {string} [context.fallbackMessage] Reporting surface's own label,
       *   used as the event title only when `error` carries no message.
       */
      captureException: function (error, context) {
        if (!window.Sentry || typeof window.Sentry.captureException !== 'function') return null;

        var hasContext = Boolean(context) && typeof context === 'object';
        var label = hasContext ? context.fallbackMessage : null;
        var normalizedError;

        // Every read of a thrown value can itself throw: a Proxy traps
        // getPrototypeOf/get/ownKeys (so even `instanceof Error` throws), a
        // getter can throw, and a null-prototype object throws on string
        // conversion. Reporting must never throw from inside an error handler
        // -- callers invoke this from `catch` blocks, so an escaping throw
        // would skip their recovery path and lose the event entirely.
        try {
          normalizedError = normalizeException(error, label);
        } catch (inspectionError) {
          normalizedError = new Error(fallbackLabel(label));
        }

        if (hasContext && typeof window.Sentry.withScope === 'function') {
          return window.Sentry.withScope(function (scope) {
            var causeExtra;
            try {
              causeExtra = describeNonErrorCause(error);
            } catch (inspectionError) {
              causeExtra = { causeType: 'uninspectable' };
            }
            var extras = causeExtra
              ? Object.assign({}, context.extra, causeExtra)
              : context.extra;
            if (context.tags && typeof scope.setTags === 'function') {
              scope.setTags(context.tags);
            }
            if (extras && typeof scope.setExtras === 'function') {
              scope.setExtras(extras);
            }
            if (context.level && typeof scope.setLevel === 'function') {
              scope.setLevel(context.level);
            }
            // Only an array of non-empty strings is a usable grouping key.
            // Sentry stringifies whatever it is handed, so `[{}]` or `[null]`
            // would regroup the event under `[object Object]`/`null` -- worse
            // than the default grouping it replaced. The length check stays:
            // `every` is vacuously true for `[]`.
            if (
              Array.isArray(context.fingerprint) &&
              context.fingerprint.length &&
              context.fingerprint.every(function (part) {
                return typeof part === 'string' && part.length > 0;
              }) &&
              typeof scope.setFingerprint === 'function'
            ) {
              scope.setFingerprint(context.fingerprint);
            }
            return window.Sentry.captureException(normalizedError);
          });
        }

        return window.Sentry.captureException(normalizedError);
      },
      captureMessage: function (message, level) {
        if (!window.Sentry || typeof window.Sentry.captureMessage !== 'function') return null;
        return window.Sentry.captureMessage(message, level || 'info');
      },
      startSpan: function (spanContext, callback) {
        if (typeof callback !== 'function') return null;
        if (window.Sentry && typeof window.Sentry.startSpan === 'function') {
          return window.Sentry.startSpan(spanContext || { name: 'theme.operation' }, callback);
        }
        return callback(null);
      },
      startSpanManual: function (spanContext, callback) {
        if (typeof callback !== 'function') return null;
        if (window.Sentry && typeof window.Sentry.startSpanManual === 'function') {
          return window.Sentry.startSpanManual(
            spanContext || { name: 'theme.operation' },
            callback
          );
        }
        return callback(null);
      }
    });
  }

  function init() {
    var cfg = buildConfig();
    var active;
    var sampleRate;
    var tracesSampleRate;
    var replaysSessionSampleRate;
    var replaysOnErrorSampleRate;
    var replayEnabled;
    var replayMaskAllText;
    var replayBlockAllMedia;

    ensureObservability();

    if (state.initialized) return;

    // The Loader Script substitutes its OWN default Sentry.init() -- DSN only,
    // no release/denyUrls/ignoreErrors/beforeSend -- whenever sentryOnLoad
    // returns WITHOUT calling Sentry.init(). Returning early here (the old
    // bot/disabled short-circuit) therefore handed traffic to that unfiltered
    // default, which silently captured the bulk of events (no release, no
    // `surface` tag) and flooded the project. So we must ALWAYS call
    // Sentry.init().
    //
    // When Sentry should be inactive (automated traffic, or config disabled) we
    // still call init(), but with `enabled: false`. A disabled client is a true
    // no-op: it sends no error/replay/transaction AND starts no session, so the
    // crash-free SESSION rate is untouched (the original reason bots were
    // skipped) -- while the init() call still claims the slot and stops the
    // loader default from ever running.
    if (!window.Sentry || typeof window.Sentry.init !== 'function') return;

    active =
      parseBoolean(cfg.enabled, false) && Boolean(cfg.dsn) && !isLikelyBot() && !isUnsupportedBrowser();

    sampleRate = parseSampleRate(cfg.sampleRate, 1.0);
    tracesSampleRate = parseSampleRate(cfg.tracesSampleRate, 0.1);
    replaysSessionSampleRate = parseSampleRate(cfg.replaysSessionSampleRate, 0.05);
    replaysOnErrorSampleRate = parseSampleRate(cfg.replaysOnErrorSampleRate, 1.0);
    replayEnabled = parseBoolean(cfg.replayEnabled, true);
    replayMaskAllText = parseBoolean(cfg.replayMaskAllText, true);
    replayBlockAllMedia = parseBoolean(cfg.replayBlockAllMedia, true);

    if (!active || !replayEnabled) {
      replaysSessionSampleRate = 0;
      replaysOnErrorSampleRate = 0;
    }

    window.Sentry.init({
      dsn: cfg.dsn,
      enabled: active,
      environment: cfg.environment || 'production',
      release: cfg.release || undefined,
      // Attach the user from the very start via initialScope rather than a
      // post-init setUser(), so the Loader Script's buffered/early errors
      // (flushed at init time) are attributed to the shopper too.
      initialScope: active ? { user: buildSentryUser(cfg) } : undefined,
      sampleRate: active ? sampleRate : 0,
      tracesSampleRate: active ? tracesSampleRate : 0,
      replaysSessionSampleRate: replaysSessionSampleRate,
      replaysOnErrorSampleRate: replaysOnErrorSampleRate,
      tracePropagationTargets: ['localhost', /^\//],
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
        /\/cdn\/shopifycloud\/privacy-banner\/storefront-banner\.js/i,
        /\/cdn\/shopifycloud\/storefront\/assets\/shop_events_listener-[^/]+\.js/i,
        /\/web-pixels@[^/]+\//i,
        /(?:^|\/\/)js\.hs-analytics\.net\/analytics\//i
      ],
      ignoreErrors: [
        'jamToggleDumpStore',
        'fix-webm-duration',
        'The page keeping the extension port is moved into back/forward cache',
        'Script error.',
        // Dynamic ES-module import() failures, worded differently per engine
        // (Chrome/Chromium and Safari/WebKit below). Transient (flaky network,
        // content blockers) or from Shopify-platform-injected modules (e.g. shop-js
        // "Sign in with Shop" cart sync). The failing frame is an inline module
        // script whose filename is the page URL, so it leaks past the
        // same-origin-inline frame allowlist -- hence message-matching here.
        // SAFE ONLY because the theme ships no dynamic import()/type=module of its
        // own (verified). ignoreErrors runs in InboundFilters BEFORE the
        // theme-owned-frame allowlist, so if first-party code ever adopts a dynamic
        // import, NARROW OR REMOVE these patterns -- otherwise a genuine first-party
        // import failure would be silently dropped. (SHOPIFY-3DX/3DY)
        /^Failed to fetch dynamically imported module/i,
        /^Importing a module script failed/i,
        // iOS in-app browsers (Instagram, Facebook) inject a native-bridge
        // script that messages the host app through `window.webkit.messageHandlers`
        // -- the WKWebView message-handler API. When that bridge is absent the
        // injected `sendDataToNative` / `sendPageShowMessage` code throws
        // `TypeError: undefined is not an object (evaluating
        // 'window.webkit.messageHandlers')` on pageshow. Its frames carry the
        // same-origin page URL with no source line and the throwing frame is
        // anonymous, so the theme-owned-frame allowlist cannot classify it; but
        // no first-party theme code ever touches window.webkit, so matching the
        // accessor on the message reliably drops it (and catches the message-only
        // capture variants the frame allowlist never sees). (SHOPIFY-368)
        /webkit\.messageHandlers/i,
        // AccessiBe accessibility widget: its installer (injected by the accessiBe
        // Shopify app, NOT in the theme -- `acsbJS` appears nowhere in theme source)
        // calls `acsbJS.init(...)` in its script onload. When accessiBe's CDN app.js
        // loads abnormally (partial/blocked, mostly in-app browsers / WKWebView),
        // the onload runs before `acsbJS` is defined -> "ReferenceError: Can't find
        // variable: acsbJS" / "acsbJS is not defined". Its inline frame carries the
        // page URL so it leaks the theme-owned-frame allowlist. SAFE: acsbJS is
        // accessiBe-only, so matching the identifier cannot drop a theme error.
        // (SHOPIFY-H6/97/PB and siblings)
        /acsbJS/i
      ],
      beforeSend: function (event) {
        var hasExceptionValues = Boolean(
          event &&
          event.exception &&
          Array.isArray(event.exception.values) &&
          event.exception.values.length > 0
        );

        if (!hasExceptionValues) return scrubEventUrls(event);
        if (eventHasThemeOwnedFrame(event)) return scrubEventUrls(event);
        return null;
      },
      beforeBreadcrumb: scrubBreadcrumb,
      integrations: active
        ? buildIntegrations(replayEnabled, replayMaskAllText, replayBlockAllMedia)
        : []
    });

    // Only wire up tagging, user context, and the real observability facade when
    // Sentry is active. For inactive (bot/disabled) sessions the no-op
    // observability installed above stays in place and nothing is ever sent.
    if (active) {
      if (typeof window.Sentry.setTag === 'function') {
        window.Sentry.setTag('surface', 'shopify-theme');
      }

      window.etObservability = createObservability();
    }

    state.initialized = true;
  }

  ensureObservability();

  window.ET = window.ET || {};
  window.ET.Sentry = freezeValue({
    init: init,
    getConfig: function () {
      return state.config || buildConfig();
    },
    // Shared so consumer assets scrub URLs the same way this file scrubs
    // breadcrumbs and request context. One implementation, one contract.
    templateUrl: templateUrl,
    shortValue: shortValue
  });

  window.sentryOnLoad = init;

  if (window.Sentry && typeof window.Sentry.init === 'function') {
    init();
  }
})();
